import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import api from '../api';
import { AlertTriangle, AlertOctagon, Sparkles } from 'lucide-react';

const MONTHS_NO = [
  'januar', 'februar', 'mars', 'april', 'mai', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'desember',
];

function formatPeriodNO(startISO, endISO) {
  if (!startISO || !endISO) return '';
  try {
    const start = new Date(startISO);
    const end = new Date(endISO);
    const month = MONTHS_NO[start.getMonth()];
    const year = start.getFullYear();
    return `Periode: ${start.getDate()}. ${month} – ${end.getDate()}. ${month} ${year}`;
  } catch {
    return '';
  }
}

function suggestUpgrade(planName) {
  const p = (planName || '').toLowerCase();
  if (p === 'start') return { next: 'Pro', minutes: 400 };
  if (p === 'pro') return { next: 'Bedrift', minutes: 1100 };
  return null;
}

function BarFill({ pct, status, height = 'h-4' }) {  // eslint-disable-line
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const color =
    status === 'critical' ? 'bg-accent-red'
    : status === 'warning' ? 'bg-accent-yellow'
    : 'bg-accent-green';
  return (
    <div className={`w-full ${height} bg-navy-900 border border-navy-600 rounded-full overflow-hidden`}>
      <div
        className={`${height} ${color} transition-all duration-500 ease-out`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function UsageWarningBanner() {
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get('/api/billing/usage')
      .then(({ data }) => { if (mounted) setUsage(data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!usage || dismissed) return null;
  const { used_minutes = 0, plan_limit = 100, pct_used = 0, status = 'ok' } = usage;
  if (status !== 'warning' && status !== 'critical') return null;
  const isCritical = status === 'critical' || pct_used >= 95;
  const dismissKey = `usage-banner-dismissed-${new Date().toISOString().slice(0,7)}-${status}`;
  if (typeof window !== 'undefined' && window.localStorage?.getItem(dismissKey)) return null;
  const handleDismiss = () => {
    try { window.localStorage.setItem(dismissKey, '1'); } catch {}
    setDismissed(true);
  };
  return (
    <div
      data-testid="usage-warning-banner"
      className={`px-4 py-2.5 flex items-center justify-between gap-3 border-b ${
        isCritical
          ? 'bg-accent-red/15 border-accent-red/40 text-accent-red'
          : 'bg-accent-yellow/15 border-accent-yellow/40 text-accent-yellow'
      }`}
    >
      <div className="flex items-center gap-2 text-sm">
        {isCritical ? <AlertOctagon size={16} /> : <AlertTriangle size={16} />}
        <span className="font-medium">
          {isCritical
            ? `Du nærmer deg minutt-grensen kritisk (${used_minutes} / ${plan_limit} min brukt)`
            : `Du nærmer deg minutt-grensen (${used_minutes} / ${plan_limit} min brukt)`}
        </span>
        <Link to="/billing" className="underline hover:opacity-80">Se fakturering</Link>
      </div>
      <button
        onClick={handleDismiss}
        className="text-xs opacity-70 hover:opacity-100 px-2 py-1"
        aria-label="Lukk"
      >✕</button>
    </div>
  );
}

export default function UsageMeter({ compact = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get('/api/billing/usage');
        if (mounted) setUsage(data);
      } catch (err) {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    if (compact) {
      return <div className="h-6 bg-navy-800 rounded animate-pulse" data-testid="usage-meter-loading" />;
    }
    return (
      <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6 animate-pulse" data-testid="usage-meter-loading">
        <div className="h-4 w-48 bg-navy-700 rounded mb-4" />
        <div className="h-4 w-full bg-navy-700 rounded" />
      </div>
    );
  }

  if (error || !usage) return null;

  const { used_minutes = 0, plan_limit = 100, plan_name = 'Start', pct_used = 0, status = 'ok' } = usage;
  const upgrade = suggestUpgrade(plan_name);

  // --- COMPACT (Dashboard) ---
  if (compact) {
    const statusDot =
      status === 'critical' ? 'bg-accent-red'
      : status === 'warning' ? 'bg-accent-yellow'
      : null;
    return (
      <div
        className="bg-navy-800 border border-navy-600 rounded-lg p-4 transition-colors hover:border-navy-700"
        data-testid="usage-meter-compact"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">
              Minutter denne måneden
            </span>
            {statusDot && <span className={`w-2 h-2 rounded-full ${statusDot}`} />}
          </div>
          <span className="text-xs font-mono text-slate-400">
            {used_minutes}/{plan_limit}
          </span>
        </div>
        <BarFill pct={pct_used} status={status} height="h-2" />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-500">{pct_used}% brukt</span>
          {statusDot && (
            <Link
              to="/billing"
              className="text-xs text-accent-blue hover:text-accent-blue-hover transition-colors"
            >
              Se fakturering
            </Link>
          )}
        </div>
      </div>
    );
  }

  // --- FULL (BillingPage) ---
  return (
    <div
      className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6"
      data-testid="usage-meter"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
            Minutt-forbruk denne måneden
          </div>
          <h2 className="font-heading text-xl font-bold text-white">
            {used_minutes} <span className="text-slate-500 font-normal text-base">av {plan_limit} minutter</span>
          </h2>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Plan</div>
          <div className="text-lg font-heading font-bold text-white">{plan_name}</div>
        </div>
      </div>

      <BarFill pct={pct_used} status={status} height="h-4" />

      <div className="flex items-center justify-between mt-3">
        <span className="text-sm text-slate-400">
          {used_minutes} av {plan_limit} minutter brukt ({pct_used}%)
        </span>
        <span className="text-xs text-slate-500">
          {formatPeriodNO(usage.period_start, usage.period_end)}
        </span>
      </div>

      {status === 'warning' && (
        <div className="mt-5 bg-accent-yellow/10 border border-accent-yellow/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-accent-yellow flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-white font-medium mb-1">
              Du nærmer deg grensen for din {plan_name}-plan.
            </div>
            {upgrade && (
              <div className="text-sm text-slate-400 mb-3">
                Vurder å oppgradere til {upgrade.next} ({upgrade.minutes} min/mnd).
              </div>
            )}
            <a
              href="mailto:support@airesepsjonen.no?subject=Oppgradering%20av%20abonnement"
              className="inline-flex items-center gap-2 px-3 py-2 bg-accent-yellow/20 hover:bg-accent-yellow/30 text-accent-yellow text-sm font-medium rounded-lg transition-colors"
            >
              <Sparkles size={14} /> Oppgrader nå
            </a>
          </div>
        </div>
      )}

      {status === 'critical' && (
        <div className="mt-5 bg-accent-red/10 border border-accent-red/30 rounded-lg p-4 flex items-start gap-3">
          <AlertOctagon size={18} className="text-accent-red flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-white font-medium mb-1">
              Du er nesten tom for minutter.
            </div>
            <div className="text-sm text-slate-400 mb-3">
              Oppgrader nå for å unngå avbrudd i tjenesten.
            </div>
            <a
              href="mailto:support@airesepsjonen.no?subject=Kritisk%3A%20Oppgradering%20av%20abonnement"
              className="inline-flex items-center gap-2 px-3 py-2 bg-accent-red hover:bg-accent-red/80 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Sparkles size={14} /> Oppgrader nå
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

BarFill.propTypes = {
  pct: PropTypes.number,
  status: PropTypes.oneOf(['ok', 'warning', 'critical']),
  height: PropTypes.string,
};

UsageMeter.propTypes = {
  compact: PropTypes.bool,
};
